import { ArrowRightLeft, RotateCcw, Search } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '../../api';
import type { TransferDirection, TransferStatus, TransferSummary } from '../../api/areas/core';
import { DataTable, Field, FilterBar, Pager, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { CustomerRef, useVocab } from '../domains/refs';
import { StatusBadge, TRANSFER, useStatusLabel } from '../domains/status';
import { nextParams } from './shared';

const LIMIT = 50;
const ANY = '__any';
const STATUSES: TransferStatus[] = ['REQUESTED', 'AUTHORIZED', 'REGISTRY_PENDING', 'APPROVED', 'REJECTED', 'CANCELLED', 'EXPIRED', 'COMPLETED'];
const DIRECTIONS: TransferDirection[] = ['IN', 'OUT'];

/** When a transfer was decided, completed, or when the registry will approve it by default. */
export function TransferNext({ t: x }: { t: Pick<TransferSummary, 'completed_at' | 'decided_at' | 'auto_approve_at'> }) {
  const { t } = useTranslation('domains');
  if (x.completed_at) return <span className="text-xs">{t('transfers.completed', { when: formatRelative(x.completed_at) })}</span>;
  if (x.decided_at) return <span className="text-xs">{t('transfers.decided', { when: formatRelative(x.decided_at) })}</span>;
  if (x.auto_approve_at) return <span className="text-xs font-medium text-warning">{t('transfers.autoApprove', { when: formatRelative(x.auto_approve_at) })}</span>;
  return <span className="text-muted-foreground">—</span>;
}

/** `/transfers`: inbound and outbound registrar transfers. */
export function TransfersPage() {
  const { t } = useTranslation('domains');
  const { t: tc } = useTranslation();
  const vocab = useVocab();
  const statusLabel = useStatusLabel();
  useDocumentTitle(t('transfers.title'));
  const [params, setParams] = useSearchParams();
  const domain = params.get('domain') ?? '';
  const status = (params.get('status') ?? '') as TransferStatus | '';
  const direction = (params.get('direction') ?? '') as TransferDirection | '';
  const before = params.get('before') ?? '';
  const [draft, setDraft] = useState(domain);
  const [draftFor, setDraftFor] = useState(domain);
  const [history, setHistory] = useState<string[]>([]);
  const result = useApi(
    (signal) =>
      api.listTransfers({ domain: domain || undefined, status: status || undefined, direction: direction || undefined, before: before || undefined, limit: LIMIT }, { signal }),
    [domain, status, direction, before],
  );
  if (draftFor !== domain) {
    setDraftFor(domain);
    setDraft(domain);
  }
  const update = (next: Record<string, string>) => setParams(nextParams(params, next));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setHistory([]);
    update({ domain: draft.trim().toLowerCase(), before: '' });
  };
  const filtered = Boolean(domain || status || direction);
  const reset = () => {
    setHistory([]);
    setDraft('');
    update({ domain: '', status: '', direction: '', before: '' });
  };

  const columns: Column<TransferSummary>[] = [
    {
      key: 'domain',
      header: t('transfers.colDomain'),
      cell: (x) => (
        <EntityRef
          label={x.unicode_name}
          to={`/transfers/${encodeURIComponent(x.id)}`}
          id={x.id}
          sub={x.unicode_name !== x.ascii_name ? <span className="font-mono">{x.ascii_name}</span> : undefined}
        />
      ),
    },
    {
      key: 'direction',
      header: t('transfers.colDirection'),
      cell: (x) => (
        <div>
          <div>{vocab('direction', x.direction)}</div>
          <div className="text-xs text-muted-foreground">{x.registry_code}</div>
        </div>
      ),
    },
    { key: 'status', header: t('transfers.colStatus'), cell: (x) => <StatusBadge map={TRANSFER} value={x.status} /> },
    { key: 'other', header: t('transfers.colOther'), className: 'min-w-40 whitespace-normal', cell: (x) => x.other_registrar ?? <span className="text-muted-foreground">—</span> },
    { key: 'customer', header: t('transfers.colCustomer'), className: 'min-w-36 whitespace-normal', cell: (x) => <CustomerRef id={x.customer_id} name={x.customer_name} /> },
    {
      key: 'requested',
      header: t('transfers.colRequested'),
      cell: (x) => (
        <div className="flex flex-col gap-0.5">
          <span className="whitespace-nowrap">{formatDateTime(x.requested_at)}</span>
          <TransferNext t={x} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('transfers.title')} crumbs={[{ label: t('transfers.crumb') }]} subtitle={t('transfers.subtitle')} />
      <form onSubmit={submit} role="search">
        <FilterBar
          actions={
            filtered ? (
              <Button type="button" variant="ghost" size="sm" onClick={reset}>
                <RotateCcw /> {tc('action.reset')}
              </Button>
            ) : null
          }
        >
          <Field label={t('transfers.filterDomain')} className="w-full sm:w-64">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t('transfers.filterDomainPlaceholder')} autoComplete="off" spellCheck={false} className="bg-card pl-8" />
            </div>
          </Field>
          <Field label={t('transfers.filterDirection')} className="w-full sm:w-56">
            <Select
              value={direction || ANY}
              onValueChange={(v) => {
                setHistory([]);
                update({ direction: v === ANY ? '' : v, before: '' });
              }}
            >
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('transfers.anyDirection')}</SelectItem>
                {DIRECTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {vocab('direction', d)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('transfers.filterStatus')} className="w-full sm:w-52">
            <Select
              value={status || ANY}
              onValueChange={(v) => {
                setHistory([]);
                update({ status: v === ANY ? '' : v, before: '' });
              }}
            >
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('transfers.anyStatus')}</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {statusLabel(TRANSFER, s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Button type="submit">
            <Search /> {t('transfers.search')}
          </Button>
        </FilterBar>
      </form>

      <Section flush title={t('transfers.tableTitle')} description={t('transfers.tableDescription')}>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={4} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <DataTable
              columns={columns}
              rows={result.data.items}
              rowKey={(x) => x.id}
              empty={
                <EmptyState title={t('transfers.empty')} icon={ArrowRightLeft}>
                  {t('transfers.emptyBody')}
                </EmptyState>
              }
            />
            {result.data.items.length > 0 || before ? (
              <Pager
                canPrevious={Boolean(before)}
                canNext={Boolean(result.data.next_before)}
                onPrevious={() => {
                  const prev = history.slice(0, -1);
                  update({ before: history[history.length - 1] ?? '' });
                  setHistory(prev);
                }}
                onNext={() => {
                  setHistory([...history, before]);
                  update({ before: result.data.next_before ?? '' });
                }}
                info={t('transfers.shown', { count: result.data.items.length })}
              />
            ) : null}
          </>
        ) : null}
      </Section>
    </>
  );
}
